import React, { memo, useState, useEffect, useRef } from 'react';

import {
  Col,
  Modal,
  Card,
  CardHeader,
  CardBody,
  Form,
  Row,
  Label,
  FormGroup,
  Input,
  Spinner,
} from 'reactstrap';

import FormButtons from '~/components/FormButtons';

import api from '~/services/api';
import notification from '~/services/notification';

import util from '~/assets/util';

const DEFAULT_STATE = {
  name: '',
  biography: '',
};

interface AuthorModalProps {
  isOpen?: any;
  id: any;
  onSave: any;
  onToggle: any;
}

function AuthorModal({ isOpen = false, id, onSave, onToggle }: AuthorModalProps) {
  const [loading, setLoading] = useState(!!id);

  const [editing, setEditing] = useState(!!id);
  const [author, setAuthor] = useState(DEFAULT_STATE);

  const inputNameRef = useRef(null);

  const handleChange = (ev) => {
    const name = ev.target.name;

    setAuthor({ ...author, [name]: ev.target.value });
  };

  const doSave = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (loading) {
      return;
    }

    setLoading(true);

    const url = 'admin/authors';

    try {
      const { data } = id ? await api.put(`${url}/${id}`, author) : await api.post(url, author);

      notification.$s(util.t('AUTHOR_SAVED'));

      setAuthor(DEFAULT_STATE);

      onSave(data?.author);
    } catch (e) {
      console.error('authorModal.doSave', e);

      notification.$e(e);
    } finally {
      setLoading(false);
    }
  };

  async function loadAuthor(id) {
    try {
      const { data } = await api.get(`admin/authors/${id}`);

      const author = data?.author;

      setAuthor(author);
    } catch (e) {
      notification.$e(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!editing) {
      inputNameRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    id && loadAuthor(id);
  }, []);

  return (
    <Modal autoFocus={false} isOpen={isOpen} toggle={onToggle} size="xl">
      <Card>
        <CardHeader>{util.t('AUTHOR')}</CardHeader>
        <CardBody>
          {loading ? (
            <Spinner color="primary" className="d-block m-auto" />
          ) : (
            <Form onSubmit={doSave}>
              <fieldset disabled={editing}>
                <Row form>
                  <Col lg="6">
                    <FormGroup>
                      <Label htmlFor="name">
                        {util.t('NAME')} <strong>*</strong>
                      </Label>

                      <Input
                        autoFocus
                        id="name"
                        name="name"
                        value={author.name}
                        onChange={handleChange}
                        innerRef={inputNameRef}
                        max="255"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row form>
                  <Col>
                    <FormGroup>
                      <Label htmlFor="biography">{util.t('BIOGRAPHY')}</Label>

                      <textarea
                        rows={5}
                        name="biography"
                        id="biography"
                        className="form-control"
                        onChange={handleChange}
                        value={author.biography}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </fieldset>
              <FormButtons
                isEditing={editing}
                isSaving={loading}
                onCancel={onToggle}
                onEdit={() => setEditing(false)}
              />
            </Form>
          )}
        </CardBody>
      </Card>
    </Modal>
  );
}

export default memo(AuthorModal);
